
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * DrawingStorageSqliteParams struct
 */
class DrawingStorageSqliteParams {
  /**
   * Initialize struct
   * @param {!string=} databaseFile
   * @param {string=} databasePassword
   * @constructor
   */
  constructor (databaseFile = '../../drawing_storage/drawing_storage.db', databasePassword = null) {
    this.DatabaseFile = databaseFile
    this.DatabasePassword = databasePassword
  }

  /**
   * Copy struct (shallow copy)
   * @this {!DrawingStorageSqliteParams}
   * @param {!DrawingStorageSqliteParams} other Other struct
   * @returns {!DrawingStorageSqliteParams} This struct
   */
  copy (other) {
    if (other.DatabaseFile != null) {
      this.DatabaseFile = other.DatabaseFile
    } else {
      this.DatabaseFile = null
    }
    if (other.DatabasePassword != null) {
      this.DatabasePassword = other.DatabasePassword
    } else {
      this.DatabasePassword = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!DrawingStorageSqliteParams}
   * @returns {!DrawingStorageSqliteParams} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new DrawingStorageSqliteParamsModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new DrawingStorageSqliteParamsModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!DrawingStorageSqliteParams}
   * @param {!DrawingStorageSqliteParams} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof DrawingStorageSqliteParams)) {
      throw new TypeError('Instance of DrawingStorageSqliteParams is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!DrawingStorageSqliteParams}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      DatabaseFile: ((this.DatabaseFile != null) ? this.DatabaseFile : null),
      DatabasePassword: ((this.DatabasePassword != null) ? this.DatabasePassword : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return DrawingStorageSqliteParams.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!DrawingStorageSqliteParams} other Object value
   * @returns {!DrawingStorageSqliteParams} Created struct
   */
  static fromObject (other) {
    return new DrawingStorageSqliteParams().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!DrawingStorageSqliteParams}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return DrawingStorageSqliteParams.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!DrawingStorageSqliteParams}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 193
  }
}

export { DrawingStorageSqliteParams };

/**
 * Fast Binary Encoding DrawingStorageSqliteParams field model
 */
class FieldModelDrawingStorageSqliteParams extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.DatabaseFile = new fbe.FieldModelString(buffer, 4 + 4)
    this.DatabasePassword = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.DatabaseFile.fbeOffset + this.DatabaseFile.fbeSize), buffer, this.DatabaseFile.fbeOffset + this.DatabaseFile.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelDrawingStorageSqliteParams}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelDrawingStorageSqliteParams}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.DatabaseFile.fbeSize + this.DatabasePassword.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelDrawingStorageSqliteParams}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.DatabaseFile.fbeExtra + this.DatabasePassword.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelDrawingStorageSqliteParams}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelDrawingStorageSqliteParams.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelDrawingStorageSqliteParams}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 193
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelDrawingStorageSqliteParams}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelDrawingStorageSqliteParams}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.DatabaseFile.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.DatabaseFile.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DatabaseFile.fbeSize

    if ((fbeCurrentSize + this.DatabasePassword.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.DatabasePassword.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DatabasePassword.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelDrawingStorageSqliteParams}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelDrawingStorageSqliteParams}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelDrawingStorageSqliteParams}
   * @param {!DrawingStorageSqliteParams} fbeValue Default value, defaults is new DrawingStorageSqliteParams()
   * @returns {!DrawingStorageSqliteParams} DrawingStorageSqliteParams value
   */
  get (fbeValue = new DrawingStorageSqliteParams()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelDrawingStorageSqliteParams}
   * @param {!DrawingStorageSqliteParams} fbeValue DrawingStorageSqliteParams value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.DatabaseFile.fbeSize) <= fbeStructSize) {
      fbeValue.DatabaseFile = this.DatabaseFile.get('../../drawing_storage/drawing_storage.db')
    } else {
      fbeValue.DatabaseFile = '../../drawing_storage/drawing_storage.db'
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DatabaseFile.fbeSize

    if ((fbeCurrentSize + this.DatabasePassword.fbeSize) <= fbeStructSize) {
      fbeValue.DatabasePassword = this.DatabasePassword.get()
    } else {
      fbeValue.DatabasePassword = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DatabasePassword.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelDrawingStorageSqliteParams}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelDrawingStorageSqliteParams}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelDrawingStorageSqliteParams}
   * @param {!DrawingStorageSqliteParams} fbeValue DrawingStorageSqliteParams value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelDrawingStorageSqliteParams}
   * @param {DrawingStorageSqliteParams} fbeValue DrawingStorageSqliteParams value
   */
  setFields (fbeValue) {
    this.DatabaseFile.set(fbeValue.DatabaseFile)
    this.DatabasePassword.set(fbeValue.DatabasePassword)
  }
}

export { FieldModelDrawingStorageSqliteParams };

/**
 * Fast Binary Encoding DrawingStorageSqliteParams model
 */
class DrawingStorageSqliteParamsModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelDrawingStorageSqliteParams(this.buffer, 4)
  }

  /**
   * Get the DrawingStorageSqliteParams model
   * @this {!DrawingStorageSqliteParamsModel}
   * @returns {!FieldModelDrawingStorageSqliteParams} model DrawingStorageSqliteParams model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!DrawingStorageSqliteParamsModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!DrawingStorageSqliteParamsModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return DrawingStorageSqliteParamsModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!DrawingStorageSqliteParamsModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelDrawingStorageSqliteParams.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!DrawingStorageSqliteParamsModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!DrawingStorageSqliteParamsModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!DrawingStorageSqliteParamsModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!DrawingStorageSqliteParamsModel}
   * @param {!DrawingStorageSqliteParams} value DrawingStorageSqliteParams value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!DrawingStorageSqliteParamsModel}
   * @param {!DrawingStorageSqliteParams} value DrawingStorageSqliteParams value, defaults is new DrawingStorageSqliteParams()
   * @return {!object} Deserialized DrawingStorageSqliteParams value and its size
   */
  deserialize (value = new DrawingStorageSqliteParams()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new DrawingStorageSqliteParams(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new DrawingStorageSqliteParams(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!DrawingStorageSqliteParamsModel}
   * @param {!number} prev Previous DrawingStorageSqliteParams model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { DrawingStorageSqliteParamsModel };
